import React from "react";
import { Link } from "react-router-dom";
import {
  COMPANY,
  SERVICES_MENU,
  FOOTER_QUICK_LINKS,
  FOOTER_LEGAL,
} from "../../lib/content";

const ColumnTitle = ({ children }) => (
  <h4
    className="mb-6 text-[11px] font-semibold uppercase"
    style={{ letterSpacing: "0.2em", color: "rgba(216,183,122,0.85)" }}
  >
    {children}
  </h4>
);

const FooterLink = ({ to, href, children, testId }) => {
  const cls =
    "inline-block text-[14px] leading-[1.5] transition-colors duration-200 hover:text-ivory";
  const style = { color: "rgba(247,246,242,0.66)" };
  if (href) {
    return (
      <a href={href} data-testid={testId} className={cls} style={style} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noopener noreferrer" : undefined}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={cls} style={style}>
      {children}
    </Link>
  );
};

export default function Footer() {
  return (
    <footer className="cinematic relative overflow-hidden" data-testid="site-footer">
      <div className="lk-container-wide relative pt-[clamp(38px,3.8vw,62px)] pb-10">
        {/* brand block */}
        <div className="grid gap-12 lg:grid-cols-[1.1fr_2fr]">
          <div>
            <img
              src="/brand/lykit-logo-white.png"
              alt={COMPANY.legalName}
              className="h-[34px] w-auto"
              loading="lazy"
            />
            <p
              className="mt-7 max-w-[34ch] font-display text-[clamp(16px,1.3vw,19px)] font-extrabold leading-[1.28] tracking-[-0.02em]"
              style={{ color: "var(--lykit-ivory)" }}
            >
              {COMPANY.tagline}
            </p>
            <p className="mt-5 text-[12px] uppercase" style={{ letterSpacing: "0.18em", color: "rgba(247,246,242,0.45)" }}>
              {COMPANY.secondaryTagline}
            </p>
          </div>

          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <nav aria-label="Quick links">
              <ColumnTitle>Quick Links</ColumnTitle>
              <ul className="space-y-3">
                {FOOTER_QUICK_LINKS.map((l) => (
                  <li key={l.label}>
                    <FooterLink to={l.to}>{l.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Services">
              <ColumnTitle>Services</ColumnTitle>
              <ul className="space-y-3">
                {SERVICES_MENU.map((l) => (
                  <li key={l.to}>
                    <FooterLink to={l.to}>{l.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Connect">
              <ColumnTitle>Connect</ColumnTitle>
              <ul className="space-y-3">
                {COMPANY.social.map((s) => (
                  <li key={s.label}>
                    <FooterLink href={s.href} testId={`footer-social-${s.label.toLowerCase()}`}>
                      {s.label}
                    </FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <div>
              <ColumnTitle>Contact</ColumnTitle>
              <a
                href={COMPANY.mapsDirections}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address-link"
                className="block text-[14px] leading-[1.6] transition-colors duration-200 hover:text-ivory"
                style={{ color: "rgba(247,246,242,0.66)" }}
              >
                {COMPANY.addressLines.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="footer-email-link"
                className="mt-4 inline-block text-[14px] transition-colors duration-200 hover:text-ivory"
                style={{ color: "rgba(216,183,122,0.9)" }}
              >
                {COMPANY.email}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 h-[1px] w-full" style={{ background: "rgba(247,246,242,0.12)" }} />

        <div className="mt-7 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <ul className="flex flex-wrap gap-x-7 gap-y-3">
            {FOOTER_LEGAL.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-legal-${l.to.split("/").pop()}`}
                  className="text-[12.5px] transition-colors duration-200 hover:text-ivory"
                  style={{ color: "rgba(247,246,242,0.5)" }}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <img src="/brand/lykit-mark.png" alt="" aria-hidden="true" className="h-[16px] w-auto" />
            <p className="text-[12.5px]" style={{ color: "rgba(247,246,242,0.5)" }}>
              &copy; 2026 LYKIT Entertainment Pvt. Ltd. All Rights Reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
